import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { divisionsApi } from '@/utils/api';
import type { Division } from '@/types';
import type { RootState } from '../index';

interface DivisionsState {
  entities: Division[];
  currentEntity: Division | null;
  loading: boolean;
  error: string | null;
}

const initialState: DivisionsState = {
  entities: [],
  currentEntity: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchDivisions = createAsyncThunk(
  'divisions/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await divisionsApi.getAll();
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch divisions');
      }
      return (response.data as { divisions: Division[] })?.divisions || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch divisions');
    }
  }
);

export const fetchDivisionById = createAsyncThunk(
  'divisions/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await divisionsApi.getById(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch division');
      }
      return (response.data as { division: Division })?.division || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch division');
    }
  }
);

export const createDivision = createAsyncThunk(
  'divisions/create',
  async (data: Partial<Division>, { rejectWithValue }) => {
    try {
      const response = await divisionsApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create division');
      }
      return (response.data as { division: Division })?.division || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create division');
    }
  }
);

export const updateDivision = createAsyncThunk(
  'divisions/update',
  async ({ id, data }: { id: string; data: Partial<Division> }, { rejectWithValue }) => {
    try {
      const response = await divisionsApi.update(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update division');
      }
      return (response.data as { division: Division })?.division || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update division');
    }
  }
);

export const deleteDivision = createAsyncThunk(
  'divisions/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await divisionsApi.delete(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to delete division');
      }
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete division');
    }
  }
);

export const updateDivisionBudget = createAsyncThunk(
  'divisions/updateBudget',
  async ({ id, data }: { id: string; data: { annualBudget?: number; budgetFiscalYear?: number } }, { rejectWithValue }) => {
    try {
      const response = await divisionsApi.updateBudget(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update division budget');
      }
      return (response.data as { division: Division })?.division || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update division budget');
    }
  }
);

const divisionsSlice = createSlice({
  name: 'divisions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEntity: (state, action: PayloadAction<Division | null>) => {
      state.currentEntity = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchDivisions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDivisions.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchDivisions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchDivisionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDivisionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntity = action.payload;
      })
      .addCase(fetchDivisionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createDivision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDivision.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.entities.unshift(action.payload);
        }
      })
      .addCase(createDivision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateDivision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDivision.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(d => d.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(updateDivision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteDivision.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDivision.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(d => d.id !== action.payload);
        if (state.currentEntity?.id === action.payload) {
          state.currentEntity = null;
        }
      })
      .addCase(deleteDivision.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Budget
      .addCase(updateDivisionBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDivisionBudget.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(d => d.id === action.payload!.id);
          if (index !== -1) {
            // Merge budget fields into existing entity
            state.entities[index] = {
              ...state.entities[index],
              annualBudget: action.payload.annualBudget,
              budgetFiscalYear: action.payload.budgetFiscalYear,
            };
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = {
              ...state.currentEntity,
              annualBudget: action.payload.annualBudget,
              budgetFiscalYear: action.payload.budgetFiscalYear,
            };
          }
        }
      })
      .addCase(updateDivisionBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentEntity } = divisionsSlice.actions;

// Selectors
export const selectAllDivisions = (state: RootState) => state.divisions.entities;
export const selectDivisionById = (state: RootState, id: string) =>
  state.divisions.entities.find(d => d.id === id);
export const selectDivisionsLoading = (state: RootState) => state.divisions.loading;
export const selectDivisionsError = (state: RootState) => state.divisions.error;
export const selectCurrentDivision = (state: RootState) => state.divisions.currentEntity;

export default divisionsSlice.reducer;
